import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

export interface DialogConfig {
  type: 'success' | 'error' | 'delete' | 'warning';
  title: string;
  subtitle?: string;
  image?: string;
  showFooter?: boolean;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  showSecondaryButton?: boolean;
}

@Component({
  selector: 'app-confirmation-dialog',
  template: `
    @if (isVisible) {
      <dialog
        #dialogRef
        class="fullscreen-dialog"
        [@dialogAnimation]="isVisible ? 'visible' : 'hidden'"
        [open]="isVisible"
        (click)="onBackdropClick($event)">
    
        <div class="dialog-content" (click)="$event.stopPropagation()">
          <!-- Close button -->
          <button class="close-btn" (click)="onClose()" type="button" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="3.1rem" height="3.1rem" viewBox="0 0 31 31" fill="none">
              <circle cx="15.9867" cy="15.3627" r="15.0141" fill="white"/>
              <path d="M10.9766 10.3535L20.967 20.344" stroke="#016B68" stroke-width="2" stroke-linecap="round"/>
              <path d="M20.9688 10.3535L10.9783 20.344" stroke="#016B68" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
    
          <!-- Dialog Header -->
          <div class="dialog-header">
            <div class="icon-container" [ngClass]="'icon-' + config.type">
              <!-- @if(config.image){  -->
              @if (config.image) {
                <img [src]="config.image" [alt]="config.type + ' icon'">
              } @else {
                <!-- Success Icon -->
                @if(config.type === 'success'){
                  <svg  xmlns="http://www.w3.org/2000/svg" width="136" height="135" viewBox="0 0 136 135" fill="none">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M67.9971 0.259766C30.8606 0.259766 0.648438 30.4719 0.648438 67.6084C0.648438 104.745 30.8606 134.957 67.9971 134.957C105.134 134.957 135.346 104.745 135.346 67.6084C135.346 30.4719 105.134 0.259766 67.9971 0.259766ZM67.9971 107.07C46.2377 107.07 28.535 89.3678 28.535 67.6084C28.535 45.849 46.2377 28.1463 67.9971 28.1463C89.7565 28.1463 107.459 45.849 107.459 67.6084C107.459 89.3678 89.7565 107.07 67.9971 107.07ZM83.4004 56.8484C84.2686 57.7139 84.2712 59.1214 83.4031 59.9896C83.4031 59.9896 83.4031 59.9896 83.4004 59.9922L65.0269 78.3658C64.1587 79.2339 62.7512 79.2339 61.8831 78.3658L52.5937 69.0764C51.7176 68.2161 51.7019 66.8086 52.5621 65.9326C53.4224 65.0565 54.8299 65.0407 55.7059 65.901C55.7165 65.9115 55.7244 65.9194 55.7349 65.9299L63.4537 73.6487L80.2593 56.8458C81.1274 55.9776 82.5349 55.9776 83.4031 56.8458L83.4004 56.8484Z" fill="#84BC47"/>
                  </svg>
                }
                <!-- Error/Delete Icon -->
                @if(config.type === 'error' || config.type === 'delete'){
                  <svg width="100" height="101" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="50" cy="50.2432" r="50" fill="white"/>
                    <path d="M49.2666 25.7588C49.5927 25.194 50.4083 25.194 50.7344 25.7588L75.2031 68.1406C75.5288 68.7052 75.1215 69.4108 74.4697 69.4111H25.5312C24.8791 69.4111 24.4719 68.7054 24.7979 68.1406L49.2666 25.7588Z" stroke="#D81831" stroke-width="4"/>
                    <path d="M51.9225 56.4818H47.9749L47.7325 39.6177H52.1649L51.9225 56.4818ZM50.018 63.7539C49.2561 63.7539 48.6328 63.5115 48.1134 63.0267C47.594 62.5419 47.3516 61.9185 47.3516 61.1567C47.3516 60.4295 47.594 59.8062 48.1134 59.3214C48.6328 58.8366 49.2561 58.5942 50.018 58.5942C50.7452 58.5942 51.3685 58.8366 51.9225 59.3214C52.442 59.8062 52.719 60.4295 52.719 61.1567C52.719 61.9185 52.442 62.5419 51.9225 63.0267C51.3685 63.5115 50.7452 63.7539 50.018 63.7539Z" fill="#D81831"/>
                  </svg>
                }
                <!-- Warning Icon -->
                @if(config.type === 'warning'){
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                }
              }
              <!-- } -->
            </div>
    
            <h2 class="dialog-title">{{ config.title }}</h2>
            @if(config.subtitle){
              <p [ngClass]="'dialog-subtitle-' + config.type" class="dialog-subtitle">{{ config.subtitle }}</p>
            }
          </div>
    
          <!-- Dialog Footer -->
          @if(config.showFooter !== false){
            <div class="dialog-footer">
              @if(config.showSecondaryButton !== false){
                <button
                  type="button"
                  class="btn btn-secondary"
                  (click)="onSecondaryAction()">
                  {{ getSecondaryButtonText() }}
                </button>
              }
              <button
                type="button"
                class="btn btn-primary"
                [ngClass]="'btn-' + config.type"
                (click)="onPrimaryAction()">
                {{ getPrimaryButtonText() }}
              </button>
            </div>
          }
        </div>
      </dialog>
    }
    
    `,
  styleUrls: ['./confirmation-dialog.scss'],
  imports: [
    NgClass
],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('dialogAnimation', [
      state('hidden', style({
        opacity: 0,
        transform: 'scale(0.8)',
        backdropFilter: 'blur(0px)'
      })),
      state('visible', style({
        opacity: 1,
        transform: 'scale(1)',
        backdropFilter: 'blur(8px)'
      })),
      transition('hidden => visible', [
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)')
      ]),
      transition('visible => hidden', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)')
      ])
    ])
  ]
})
export class ConfirmationDialog implements OnInit, OnDestroy {
  @Input() config: DialogConfig = {
    type: 'success',
    title: '',
    showFooter: true,
    showSecondaryButton: true
  };

  @Input() isVisible: boolean = false;

  @Output() primaryAction = new EventEmitter<void>();
  @Output() secondaryAction = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  private defaultTexts = {
    success: {
      primary: 'Continue',
      secondary: 'Cancel'
    },
    error: {
      primary: 'Try Again',
      secondary: 'Cancel'
    },
    delete: {
      primary: 'Yes, Delete!',
      secondary: 'No, Keep It.'
    },
    warning: {
      primary: 'Proceed',
      secondary: 'Cancel'
    }
  };

  ngOnInit() {
    // Prevent body scroll when dialog is open
    if (this.isVisible) {
      document.body.style.overflow = 'hidden';
    }
  }

  ngOnDestroy() {
    // Restore body scroll
    document.body.style.overflow = '';
  }

  getPrimaryButtonText(): string {
    return this.config.primaryButtonText || this.defaultTexts[this.config.type]?.primary || 'OK';
  }

  getSecondaryButtonText(): string {
    return this.config.secondaryButtonText || this.defaultTexts[this.config.type]?.secondary || 'Cancel';
  }

  onPrimaryAction() {
    this.primaryAction.emit();
  }

  onSecondaryAction() {
    this.secondaryAction.emit();
  }

  onClose() {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}


import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NgClass } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class DialogService {
  private dialogConfig$ = new BehaviorSubject<DialogConfig | null>(null);
  private isVisible$ = new BehaviorSubject<boolean>(false);

  public dialogConfig = this.dialogConfig$.asObservable();
  public isVisible = this.isVisible$.asObservable();

  showDialog(config: DialogConfig) {
    this.dialogConfig$.next(config);
    setTimeout(() => {
      this.isVisible$.next(true);
    }, 50); // Small delay for smooth animation
  }

  hideDialog() {
    this.isVisible$.next(false);
    setTimeout(() => {
      this.dialogConfig$.next(null);
    }, 300); // Wait for animation to complete
  }

  // Utility methods for common dialogs
  showDeleteDialog(title: string, subtitle?: string) {
    this.showDialog({
      type: 'delete',
      title,
      subtitle,
      showFooter: true
    });
  }

  showSuccessDialog(title: string, subtitle?: string) {
    this.showDialog({
      type: 'success',
      title,
      subtitle,
      showFooter: true,
      showSecondaryButton: false
    });
  }

  showErrorDialog(title: string, subtitle?: string) {
    this.showDialog({
      type: 'error',
      title,
      subtitle,
      showFooter: true
    });
  }
}

// Usage example in a component:
/*
export class ExampleComponent {
  constructor(private dialogService: DialogService) {}


}
*/
